/**
 * Ported VERBATIM from apr_template `src/pages/PhaseEffortPlanning.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import './ApplicationDependencies.css';
import { useAuth } from '../compat/AuthContext';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsType } from '../workspace/ws';
import type { WsTone } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const PhaseEffortPlanningHelp: Help = {
  title: 'Phase Effort Planning',
  icon: '📈',
  purpose:
    'Record the estimated <strong>duration (in days)</strong> for each migration phase of an application, giving the roadmap the timeline it needs to schedule delivery.',
  doHere: [
    'Click <strong>&#10133; Add Effort Record</strong>, pick an integration group and application, then enter days for the phases that apply.',
    'Filter the table by <strong>Grouping ID</strong>, <strong>Application ID</strong> and <strong>Status</strong> (defaults to Need Approval).',
    'Bulk-load via <strong>Download Template</strong> then <strong>Upload File</strong>; <strong>Download Data</strong> exports all records.',
    'Use <strong>&#128065;</strong> / <strong>&#9999;&#65039;</strong> / <strong>&#128465;&#65039;</strong> to view, edit or delete &mdash; locked apps are view-only.',
  ],
  fits: 'Integration &rarr; Resource Planning &rarr; Phase Effort Planning',
  firstTime:
    'Add an effort record: choose a group and application, then fill duration in days for each relevant phase.',
  sections: [
    {
      type: 'defs',
      label: 'Deployment status locks',
      items: [
        { term: 'Need Approval', color: '#757575', def: 'Editable &mdash; not yet approved for delivery.' },
        { term: 'Approved+', color: '#2e7d32', def: 'Approved, In Development, Deployed or On Hold apps are locked; effort becomes read-only.' },
      ],
    },
  ],
  links: [
    { href: '/integration-framework/resource-allocation', label: 'Resource Allocation' },
    { href: '/integration-framework/role-utilization', label: 'Role Utilization' },
  ],
};

interface PhaseEffortRecord {
  effort_id: number;
  application_id_code: string;
  application_name?: string;
  integration_group_id?: string;
  group_name?: string;
  phase_name: 'Assessment_Planning' | 'Design_Architecture' | 'Migration_Execution' | 'Testing_Validation' | 'GoLive_Optimization';
  estimated_duration_days: number;
  last_updated_date?: string;
  updated_by?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface ApplicationOption {
  application_id: string;
  description: string;
  disposition?: string;
}

/*
 * PORT NOTE — owner ruling 2026-11-03: the Approved-and-beyond lock belongs to an
 * application IN ONE integration group, as Roadmap Approval records it. The source keyed
 * it by application alone, so approving an application in one group locked it out of
 * every other group's effort planning.
 */
const lockKey = (applicationId: string, groupId?: string | null) => `${groupId || ''}|${applicationId}`;

/** A roadmap overlap with the same application's same phase in another group (GET /overlaps). */
interface PhaseOverlap {
  effort_id: number;
  other_group_id: string;
  other_group_name?: string;
  start_date: string;
  end_date: string;
  other_start_date: string;
  other_end_date: string;
}

// Helper function to get disposition badge color
// PORT NOTE: a disposition badge is a FILLED chip in the Disposition Matrix colour, with
// readable text on it (§1.9.3 rules 2-3) — not the source's hard-coded wash.
const getDispositionColor = (disposition?: string) => {
  const known = matrixColor(disposition);
  // DW: "named by the matrix" = not the matrix's own neutral fallback; unnamed → kit neutral pair.
  return disposition && known.base !== matrixColor(undefined).base ? { bg: known.base, color: known.onBase } : { bg: WS_STATUS.neutral.fill, color: WS_STATUS.neutral.ink };
};

// Helper function to get deployment status badge color
// DW: each status is a kit tone pair (green → success, blue → info, orange → warning, grey → neutral).
const getDeploymentStatusColor = (status?: string) => {
  const tone: WsTone = (() => {
    switch (status) {
      case 'Approved': return 'success';
      case 'In Development': return 'info';
      case 'Deployed': return 'success';
      case 'On Hold': return 'warning';
      default: return 'neutral';
    }
  })();
  return { bg: WS_STATUS[tone].fill, color: WS_STATUS[tone].ink };
};

const PhaseEffortPlanning: React.FC = () => {
  const { currentUser } = useAuth();
  const { confirm, notify, dialogs } = useWsDialogs();
  const [records, setRecords] = useState<PhaseEffortRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  // Map of lockKey(application, group) to deployment_status for applications past planning stage
  const [lockedApplications, setLockedApplications] = useState<Map<string, string>>(new Map());
  // effort_id -> its roadmap overlaps with the same application in other groups
  const [overlaps, setOverlaps] = useState<Map<number, PhaseOverlap[]>>(new Map());

  // Helper function to get current user's full name (updated)
  const getUserFullName = () => {
    if (currentUser?.name) {
      return currentUser.name;
    } else if (currentUser?.email) {
      return currentUser.email;
    }
    return 'Unknown User';
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  // Status filter: 'need_approval' (default), 'approved', 'in_development', 'deployed', 'on_hold', 'all'
  const [filterStatus, setFilterStatus] = useState<string>('need_approval');
  const [editingRecord, setEditingRecord] = useState<PhaseEffortRecord | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [formData, setFormData] = useState({
    effortId: '',
    applicationIdCode: '',
    groupingId: '',
    phaseName: '',
    estimatedDurationDays: '',
    updatedBy: ''
  });

  const [bulkFormData, setBulkFormData] = useState({
    applicationIdCode: '',
    groupingId: '',
    phases: {
      Assessment_Planning: { duration: '' },
      Design_Architecture: { duration: '' },
      Migration_Execution: { duration: '' },
      Testing_Validation: { duration: '' },
      GoLive_Optimization: { duration: '' }
    }
  });

  useEffect(() => {
    fetchRecords();
    fetchIntegrationGroups();
    fetchLockedApplications();
  }, []);

  // Fetch applications that are locked (Approved and onwards - cannot add/edit effort)
  const fetchLockedApplications = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      const result = await response.json();
      // API returns array directly, not wrapped in { success, data }
      const dataArray = Array.isArray(result) ? result : (result.data || []);
      // Lock applications that are Approved or beyond (In Development, Deployed, On Hold)
      const lockedStatuses = ['Approved', 'In Development', 'Deployed', 'On Hold'];
      const locked = new Map<string, string>();
      dataArray
        .filter((r: any) => lockedStatuses.includes(r.deployment_status))
        .forEach((r: any) => {
          locked.set(lockKey(r.application_id, r.integration_group_id), r.deployment_status);
        });
      setLockedApplications(locked);
    } catch (err) {
      console.error('Error fetching locked applications:', err);
    }
  };

  // Load applications when grouping ID changes in bulk form
  useEffect(() => {
    if (bulkFormData.groupingId) {
      fetchApplicationsForGroup(bulkFormData.groupingId);
    } else {
      setApplications([]);
    }
  }, [bulkFormData.groupingId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups?scope=planning');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  // Roadmap overlaps with other groups; a failure leaves the table without warning triangles
  // rather than failing the screen.
  const fetchOverlaps = async () => {
    try {
      const response = await authFetch('/api/phase-effort-planning/overlaps');
      if (!response.ok) return;
      const rows: PhaseOverlap[] = await response.json();
      const byEffort = new Map<number, PhaseOverlap[]>();
      for (const row of rows) {
        byEffort.set(row.effort_id, [...(byEffort.get(row.effort_id) || []), row]);
      }
      setOverlaps(byEffort);
    } catch (err) {
      console.error('Error loading phase overlaps:', err);
    }
  };

  const fetchRecords = async () => {
    // Always refresh locked applications when fetching records
    fetchLockedApplications();
    fetchOverlaps();

    try {
      const response = await authFetch('/api/phase-effort-planning');
      // Don't treat 404 as an error - it just means no data
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort by Effort ID in ascending order
        data.sort((a: PhaseEffortRecord, b: PhaseEffortRecord) => a.effort_id - b.effort_id);
        setRecords(data);
      } else {
        // API returned non-array (empty or error), treat as empty
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      // Only set error for actual failures, not empty data
      console.error('Error fetching phase effort records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0]!.split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i]!.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/phase-effort-planning/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} phase effort records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Application ID/Code,Grouping ID,Phase Name,Estimated Duration Days,Updated By
APP1954,IG001,Assessment_Planning,15,John Smith
APP8416,IG002,Design_Architecture,30,Jane Doe`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'phase_effort_planning_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      notify('No data to download', 'info');
      return;
    }

    // Match template format: Application ID/Code,Grouping ID,Phase Name,Estimated Duration Days,Updated By
    const headers = ['Application ID/Code', 'Grouping ID', 'Phase Name', 'Estimated Duration Days', 'Updated By'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(record.integration_group_id || '').replace(/"/g, '""')}"`,
        `"${(record.phase_name || '').replace(/"/g, '""')}"`,
        record.estimated_duration_days || 0,
        `"${(record.updated_by || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phase_effort_planning_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.applicationIdCode) {
      setError('Application ID Code is required');
      return;
    }

    if (!formData.groupingId || !formData.groupingId.trim()) {
      setError('Grouping ID is required and cannot be empty');
      return;
    }

    if (!formData.estimatedDurationDays) {
      setError('Estimated Duration Days is required');
      return;
    }

    try {
      const payload = {
        effort_id: formData.effortId ? parseInt(formData.effortId) : null,
        application_id_code: formData.applicationIdCode,
        integration_group_id: formData.groupingId.trim(),
        phase_name: formData.phaseName || 'Assessment_Planning',
        estimated_duration_days: formData.estimatedDurationDays ? parseInt(formData.estimatedDurationDays) : null,
        updated_by: formData.updatedBy || ''
      };

      if (editingRecord) {
        const response = await authFetch(`/api/phase-effort-planning/${editingRecord.effort_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }
        setSuccess('Phase effort record updated successfully');
      } else {
        const response = await authFetch('/api/phase-effort-planning', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }
        setSuccess('Phase effort record created successfully');
      }

      fetchRecords();
      
      // Clear form but keep modal open
      setFormData({
        effortId: '',
        applicationIdCode: '',
        groupingId: '',
        phaseName: '',
        estimatedDurationDays: '',
        updatedBy: ''
      });
      
      // Clear any error messages
      setError('');
    } catch (err: any) {
      console.error('Failed to save phase effort record:', err);
      setError(err.message || 'Failed to save phase effort record');
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!bulkFormData.applicationIdCode) {
        setError('Application ID/Code is required');
        return;
      }

      if (!bulkFormData.groupingId || !bulkFormData.groupingId.trim()) {
        setError('Grouping ID is required and cannot be empty');
        return;
      }

      // Check if application is locked (Approved or beyond)
      if (lockedApplications.has(lockKey(bulkFormData.applicationIdCode, bulkFormData.groupingId.trim()))) {
        const lockStatus = lockedApplications.get(lockKey(bulkFormData.applicationIdCode, bulkFormData.groupingId.trim()));
        setError(`Cannot add effort records - application "${bulkFormData.applicationIdCode}" is in status "${lockStatus}". Applications that are Approved or beyond cannot have effort records added.`);
        return;
      }

      const recordsToCreate: Array<{
        application_id_code: string;
        integration_group_id: string;
        phase_name: string;
        estimated_duration_days: number;
        updated_by: string;
      }> = [];
      
      // Check each phase and create records for those with duration and team size
      Object.entries(bulkFormData.phases).forEach(([phaseName, phaseData]) => {
        if (phaseData.duration) {
          recordsToCreate.push({
            application_id_code: bulkFormData.applicationIdCode,
            integration_group_id: bulkFormData.groupingId.trim(),
            phase_name: phaseName,
            estimated_duration_days: parseInt(phaseData.duration),
            updated_by: getUserFullName()
          });
        }
      });

      if (recordsToCreate.length === 0) {
        setError('Please fill in duration for at least one phase');
        return;
      }

      // Check for existing records (duplicates)
      // PORT NOTE — owner ruling 2026-11-03: only the SAME group is a duplicate and blocks.
      // The same phase planned in ANOTHER group is allowed after a warning, because several
      // projects (integration groups) may consolidate into one application.
      const phaseLabelMap: {[key: string]: string} = {
        'Assessment_Planning': 'Assessment & Planning',
        'Design_Architecture': 'Design & Architecture',
        'Migration_Execution': 'Migration Execution',
        'Testing_Validation': 'Testing & Validation',
        'GoLive_Optimization': 'Go-Live & Optimization'
      };
      const duplicates: string[] = [];
      const plannedElsewhere = new Map<string, string[]>(); // other group label -> phase labels
      for (const record of recordsToCreate) {
        const samePhase = records.filter(r =>
          r.application_id_code === record.application_id_code &&
          r.phase_name === record.phase_name
        );
        const phaseLabel = phaseLabelMap[record.phase_name] || record.phase_name;
        if (samePhase.some(r => (r.integration_group_id || '') === record.integration_group_id)) {
          duplicates.push(phaseLabel);
          continue;
        }
        for (const r of samePhase) {
          const groupLabel = r.group_name ? `${r.integration_group_id} – ${r.group_name}` : (r.integration_group_id || 'no group');
          plannedElsewhere.set(groupLabel, [...(plannedElsewhere.get(groupLabel) || []), phaseLabel]);
        }
      }

      if (duplicates.length > 0) {
        setError(`Cannot create records - the following phase(s) already exist for application "${bulkFormData.applicationIdCode}" in this integration group: ${duplicates.join(', ')}. Please remove existing records first or use the edit function to update them.`);
        return;
      }

      if (plannedElsewhere.size > 0) {
        const where = Array.from(plannedElsewhere.entries())
          .map(([group, phases]) => `${group}: ${phases.join(', ')}`)
          .join('; ');
        const proceed = await confirm(
          `Application "${bulkFormData.applicationIdCode}" is already planned in another integration group (${where}). ` +
          'The same phase planned in more than one group may overlap on the roadmap; an overlap is marked with a warning triangle once both groups are scheduled.',
          { title: 'Already planned in another integration group', confirmLabel: 'Create anyway' }
        );
        if (!proceed) return;
      }

      // Create records sequentially
      let created = 0;
      for (const record of recordsToCreate) {
        const response = await authFetch('/api/phase-effort-planning', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(record)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to create record for ${record.phase_name}: ${errorData.error}`);
        }
        created++;
      }

      setSuccess(`Successfully created ${created} phase effort records`);
      fetchRecords();
      
      // Clear form
      setBulkFormData({
        applicationIdCode: '',
        groupingId: '',
        phases: {
          Assessment_Planning: { duration: '' },
          Design_Architecture: { duration: '' },
          Migration_Execution: { duration: '' },
          Testing_Validation: { duration: '' },
          GoLive_Optimization: { duration: '' }
        }
      });
      setError('');
    } catch (err: any) {
      console.error('Failed to create phase effort records:', err);
      setError(err.message || 'Failed to create phase effort records');
    }
  };

  const handleEdit = (record: PhaseEffortRecord) => {
    setEditingRecord(record);
    setViewMode(false);
    setFormData({
      effortId: record.effort_id.toString(),
      applicationIdCode: record.application_id_code,
      groupingId: record.integration_group_id || '',
      phaseName: record.phase_name,
      estimatedDurationDays: record.estimated_duration_days?.toString() || '',
      updatedBy: record.updated_by || ''
    });
    setShowForm(true);
  };

  const handleView = (record: PhaseEffortRecord) => {
    setEditingRecord(record);
    setViewMode(true);
    setFormData({
      effortId: record.effort_id.toString(),
      applicationIdCode: record.application_id_code,
      groupingId: record.integration_group_id || '',
      phaseName: record.phase_name,
      estimatedDurationDays: record.estimated_duration_days?.toString() || '',
      updatedBy: record.updated_by || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (await confirm('Are you sure you want to delete this phase effort record?', { tone: 'destructive' })) {
      try {
        const response = await authFetch(`/api/phase-effort-planning/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Phase effort record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete phase effort record');
      }
    }
  };

  const handleDeleteAll = () => {
    setShowDeleteAllModal(true);
    setDeletePassword('');
  };

  const handleConfirmDeleteAll = async () => {
    if (deletePassword === 'DELETE2024!') {
      try {
        const response = await authFetch('/api/phase-effort-planning/all', {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete all');
        setSuccess('All phase effort records deleted successfully');
        fetchRecords();
        setShowDeleteAllModal(false);
        setDeletePassword('');
      } catch (err: any) {
        setError(err.message || 'Failed to delete all phase effort records');
      }
    } else {
      notify('Invalid password. Deletion cancelled.', 'error');
      setDeletePassword('');
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeletePassword('');
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setFormData({
      effortId: '',
      applicationIdCode: '',
      groupingId: '',
      phaseName: '',
      estimatedDurationDays: '',
      updatedBy: ''
    });
    setBulkFormData({
      applicationIdCode: '',
      groupingId: '',
      phases: {
        Assessment_Planning: { duration: '' },
        Design_Architecture: { duration: '' },
        Migration_Execution: { duration: '' },
        Testing_Validation: { duration: '' },
        GoLive_Optimization: { duration: '' }
      }
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setViewMode(false);
    setFormData({
      effortId: '',
      applicationIdCode: '',
      groupingId: '',
      phaseName: '',
      estimatedDurationDays: '',
      updatedBy: ''
    });
    setBulkFormData({
      applicationIdCode: '',
      groupingId: '',
      phases: {
        Assessment_Planning: { duration: '' },
        Design_Architecture: { duration: '' },
        Migration_Execution: { duration: '' },
        Testing_Validation: { duration: '' },
        GoLive_Optimization: { duration: '' }
      }
    });
    setError('');
  };

  const selectedBulkApp = bulkFormData.applicationIdCode
    ? applications.find(a => a.application_id === bulkFormData.applicationIdCode)
    : undefined;

  return (
    <div className="page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <HelpPanel help={PhaseEffortPlanningHelp} />
      {/* Header - Sticky */}
      <PageHeader title={<>Effort Planning</>} subtitle={<>Manage migration phase effort planning and resource allocation</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
<button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new phase effort record"
            >
              <Icon icon={icons.Plus} size={20} /> Add Effort Record
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              <Icon icon={icons.ClipboardList} size={20} /> Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import phase effort records from CSV"
            >
              <Icon icon={icons.Upload} size={20} /> Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
              >
                <Icon icon={icons.Download} size={20} /> Download Data
              </button>
            )}
      </div>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading phase effort records...
        </div>
      )}

      {!loading && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {/* Filter Section - Side by side */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {/* Grouping ID Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ ...wsType('label'), minWidth: '100px' }}>Grouping ID:</label>
              <input
                type="text"
                value={filterGroupingId}
                onChange={(e) => setFilterGroupingId(e.target.value)}
                placeholder="Filter by grouping..."
                style={{ width: '300px' }}
              />
              {filterGroupingId && (
                <button
                  className="btn"
                  onClick={() => setFilterGroupingId('')}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Application ID Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ ...wsType('label'), minWidth: '100px' }}>Application ID:</label>
              <input
                type="text"
                value={filterApplicationId}
                onChange={(e) => setFilterApplicationId(e.target.value)}
                placeholder="Filter by application..."
                style={{ width: '300px' }}
              />
              {filterApplicationId && (
                <button
                  className="btn"
                  onClick={() => setFilterApplicationId('')}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ ...wsType('label'), minWidth: '60px' }}>Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ minWidth: '180px', cursor: 'pointer' }}
              >
                <option value="need_approval">Need Approval</option>
                <option value="approved">Approved</option>
                <option value="in_development">In Development</option>
                <option value="deployed">Deployed</option>
                <option value="on_hold">On Hold</option>
                <option value="all">All Statuses</option>
              </select>
            </div>
            
            {/* Filter Results Counter and Clear All Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
              <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                {(() => {
                  const filteredRecords = records.filter(record => {
                    const groupingMatch = !filterGroupingId || record.integration_group_id?.toLowerCase().includes(filterGroupingId.toLowerCase());
                    const applicationMatch = !filterApplicationId ||
                      record.application_id_code.toLowerCase().includes(filterApplicationId.toLowerCase()) ||
                      record.application_name?.toLowerCase().includes(filterApplicationId.toLowerCase());

                    // Status filter logic
                    const appStatus = lockedApplications.get(lockKey(record.application_id_code, record.integration_group_id));
                    let statusMatch = true;
                    if (filterStatus === 'need_approval') {
                      // Show applications that don't have a status OR have "Need Approval" status
                      statusMatch = !appStatus || appStatus === 'Need Approval';
                    } else if (filterStatus === 'approved') {
                      statusMatch = appStatus === 'Approved';
                    } else if (filterStatus === 'in_development') {
                      statusMatch = appStatus === 'In Development';
                    } else if (filterStatus === 'deployed') {
                      statusMatch = appStatus === 'Deployed';
                    } else if (filterStatus === 'on_hold') {
                      statusMatch = appStatus === 'On Hold';
                    }

                    return groupingMatch && applicationMatch && statusMatch;
                  });
                  return `Showing ${filteredRecords.length} of ${records.length}`;
                })()}
              </span>
              {(filterGroupingId || filterApplicationId || filterStatus !== 'need_approval') && (
                <button
                  className="btn"
                  onClick={() => {
                    setFilterGroupingId('');
                    setFilterApplicationId('');
                    setFilterStatus('need_approval');
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon"><Icon icon={icons.BarChart3} size={24} /></div>
            <h3>No Phase Effort Records Found</h3>
            <p>Use the action cards above to add phase effort records or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '70px', maxWidth: '70px' }}>Effort ID</th>
                  <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>Grouping</th>
                  <th style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>Application</th>
                  <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Phase Name</th>
                  <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '60px', maxWidth: '60px' }}>Duration</th>
                  <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.filter(record => {
                  const groupingMatch = !filterGroupingId || record.integration_group_id?.toLowerCase().includes(filterGroupingId.toLowerCase());
                  const applicationMatch = !filterApplicationId ||
                    record.application_id_code.toLowerCase().includes(filterApplicationId.toLowerCase()) ||
                    record.application_name?.toLowerCase().includes(filterApplicationId.toLowerCase());

                  // Status filter logic
                  const appStatus = lockedApplications.get(lockKey(record.application_id_code, record.integration_group_id));
                  let statusMatch = true;
                  if (filterStatus === 'need_approval') {
                    // Show applications that don't have a status OR have "Need Approval" status
                    statusMatch = !appStatus || appStatus === 'Need Approval';
                  } else if (filterStatus === 'approved') {
                    statusMatch = appStatus === 'Approved';
                  } else if (filterStatus === 'in_development') {
                    statusMatch = appStatus === 'In Development';
                  } else if (filterStatus === 'deployed') {
                    statusMatch = appStatus === 'Deployed';
                  } else if (filterStatus === 'on_hold') {
                    statusMatch = appStatus === 'On Hold';
                  }
                  // 'all' shows everything

                  return groupingMatch && applicationMatch && statusMatch;
                }).map(record => (
                  <tr key={record.effort_id}>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '70px', maxWidth: '70px' }}>
                      {record.effort_id}
                    </td>
                    <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{record.integration_group_id || '-'}</div>
                        {record.group_name && (
                          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{record.group_name}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{record.application_id_code}</div>
                        {record.application_name && (
                          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{record.application_name}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {record.phase_name.replace('_', ' ')}
                      {overlaps.has(record.effort_id) && (
                        <span
                          data-testid="phase-overlap-warning"
                          title={(overlaps.get(record.effort_id) || [])
                            .map(o => `Overlaps ${o.other_group_id}${o.other_group_name ? ` – ${o.other_group_name}` : ''}: ` +
                              `this group ${o.start_date} to ${o.end_date}, other group ${o.other_start_date} to ${o.other_end_date}`)
                            .join('\n') + '\nClears when the dates no longer overlap.'}
                          style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '6px', color: WS_STATUS.warning.ink }}
                        >
                          <Icon icon={icons.AlertTriangle} size={16} />
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '60px', maxWidth: '60px' }}>{record.estimated_duration_days || '-'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        {/* Show deployment status badge if locked */}
                        {lockedApplications.has(lockKey(record.application_id_code, record.integration_group_id)) && (
                          <span
                            style={{
                              ...WS_CHIP,
                              backgroundColor: getDeploymentStatusColor(lockedApplications.get(lockKey(record.application_id_code, record.integration_group_id))).bg,
                              color: getDeploymentStatusColor(lockedApplications.get(lockKey(record.application_id_code, record.integration_group_id))).color,
                              marginRight: '6px'
                            }}
                            title="This application is locked - cannot edit/delete effort records"
                          >
                            {lockedApplications.get(lockKey(record.application_id_code, record.integration_group_id))}
                          </span>
                        )}
                        <IconButton
                          icon={icons.Eye}
                          label="View details"
                          title="View details"
                          onClick={() => handleView(record)}
                        />
                        {!lockedApplications.has(lockKey(record.application_id_code, record.integration_group_id)) && (
                          <>
                            <IconButton
                              icon={icons.Pencil}
                              label="Edit record"
                              title="Edit record"
                              onClick={() => handleEdit(record)}
                            />
                            <IconButton
                              icon={icons.Trash2}
                              label="Delete record"
                              title="Delete record"
                              onClick={() => handleDelete(record.effort_id)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload — a right-side panel (Decision Workspace Sheet, double width: it holds a wide preview table). */}
      <Sheet
        open={showUploadModal && previewData.length > 0}
        title="Upload Phase Effort Records"
        onClose={() => {
          setShowUploadModal(false);
          setPreviewData([]);
        }}
        width={1062}
        actions={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setShowUploadModal(false);
                setPreviewData([]);
              }}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleBulkImport}>
              <Icon icon={icons.Upload} size={20} /> Import {previewData.length} Phase Effort Records
            </button>
          </>
        }
      >
              <p style={{ marginBottom: '16px', ...wsType('table'), color: WS_COLOR.text }}>Found {previewData.length} phase effort records to import</p>
              <div style={{ 
                maxHeight: '400px', 
                overflowY: 'auto', 
                marginBottom: '16px', 
                border: `1px solid ${WS_COLOR.border}`, 
                borderRadius: WS_RADIUS.field 
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: WS_COLOR.surfaceSubtle }}>
                      <th style={PREVIEW_TH}>Application ID/Code</th>
                      <th style={PREVIEW_TH}>Grouping ID</th>
                      <th style={PREVIEW_TH}>Phase Name</th>
                      <th style={PREVIEW_TH}>Estimated Duration Days</th>
                      <th style={PREVIEW_TH}>Updated By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        <td style={PREVIEW_TD}>{row['Application ID/Code'] || ''}</td>
                        <td style={PREVIEW_TD}>{row['Grouping ID'] || ''}</td>
                        <td style={PREVIEW_TD}>{row['Phase Name'] || ''}</td>
                        <td style={PREVIEW_TD}>{row['Estimated Duration Days'] || ''}</td>
                        <td style={PREVIEW_TD}>{row['Updated By'] || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 5 && (
                  <p style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'center', marginTop: '8px' }}>
                    Showing first 5 records. {previewData.length - 5} more records will be imported.
                  </p>
                )}
              </div>
      </Sheet>

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="md-modal">
          <div className="md-modal-content">
            <div className="md-modal-header">
              <h2>Confirm Delete All Phase Effort Records</h2>
              <IconButton
                icon={icons.X}
                label="Close"
                className="md-close-button"
                onClick={handleCancelDeleteAll}
              />
            </div>
            <div className="md-modal-body">
              <p>This action will permanently delete all phase effort records from the database.</p>
              <div style={{ margin: '20px 0' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Banner tone="warning">
                    <p><strong>Warning:</strong></p>
                    <p>You are about to permanently delete <strong>{records.length} phase effort records</strong> from the database.</p>
                    <p>This action cannot be undone.</p>
                  </Banner>
                </div>
                <label>
                  <strong>Type "DELETE2024!" to confirm:</strong>
                  <input
                    type="text"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="md-input"
                    style={{ marginTop: '8px' }}
                  />
                </label>
              </div>
            </div>
            <div className="md-modal-footer">
              <button 
                className="md-button"
                onClick={handleCancelDeleteAll}
              >
                Cancel
              </button>
              <button 
                className="md-button md-button--primary"
                onClick={handleConfirmDeleteAll}
                disabled={deletePassword !== 'DELETE2024!'}
              >
                <Icon icon={icons.Trash2} size={20} /> Delete All Records
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase Effort Record Form — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showForm}
        title={viewMode ? 'View Effort Record' : editingRecord ? 'Edit Effort Record' : 'Add New Effort Record'}
        onClose={handleCloseForm}
        width={531}
        actions={
          editingRecord ? (
            <>
              <button type="button" className="btn" onClick={handleCloseForm}>
                {viewMode ? 'Close' : 'Cancel'}
              </button>
              {!viewMode && (
                <button type="submit" form="pep-edit-form" className="btn btn-primary">
                  Update Effort Record
                </button>
              )}
            </>
          ) : (
            <>
              <button type="button" className="btn" onClick={handleCloseForm}>
                Cancel
              </button>
              <button type="submit" form="pep-bulk-form" className="btn btn-primary">
                Create Effort Records
              </button>
            </>
          )
        }
      >
              {editingRecord ? (
                // Single record edit form
                <form id="pep-edit-form" onSubmit={handleSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px' }}>
                      {error}
                    </div>
                  )}

                  <div className="md-form-section">
                    <h3 style={SECTION_HEADING}>Basic Information</h3>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label>Effort ID (Primary Key)</label>
                        <input
                          type="number"
                          className="md-input"
                          value={formData.effortId}
                          onChange={(e) => setFormData({...formData, effortId: e.target.value})}
                          placeholder="Leave empty for auto-increment"
                          disabled={!!editingRecord}
                        />
                      </div>
                      <div className="md-form-group">
                        <label>Grouping ID *</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{ backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}
                          value={formData.groupingId}
                          onChange={(e) => setFormData({...formData, groupingId: e.target.value})}
                          required
                          placeholder="e.g., IG001"
                          disabled={viewMode}
                        />
                      </div>
                      <div className="md-form-group">
                        <label>Application ID/Code *</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{ backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}
                          value={formData.applicationIdCode}
                          onChange={(e) => setFormData({...formData, applicationIdCode: e.target.value})}
                          required
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label>Updated By</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{ backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}
                          value={formData.updatedBy}
                          onChange={(e) => setFormData({...formData, updatedBy: e.target.value})}
                          placeholder="Your name"
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                  </div>

                    <div className="md-form-section">
                      <h3 style={SECTION_HEADING}>Phase Details</h3>
                      <div className="md-form-row">
                        <div className="md-form-group">
                          <label>Phase Name</label>
                          <select
                            className="md-select"
                            style={{ backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}
                            value={formData.phaseName}
                            onChange={(e) => setFormData({...formData, phaseName: e.target.value})}
                            disabled={viewMode}
                          >
                            <option value="">Select Phase</option>
                            <option value="Assessment_Planning">Assessment & Planning</option>
                            <option value="Design_Architecture">Design & Architecture</option>
                            <option value="Migration_Execution">Migration Execution</option>
                            <option value="Testing_Validation">Testing & Validation</option>
                            <option value="GoLive_Optimization">Go-Live & Optimization</option>
                          </select>
                        </div>
                      </div>
                    </div>

                  <div className="md-form-section">
                    <h3 style={SECTION_HEADING}>Duration</h3>
                    <div className="md-form-row">
                      <div className="md-form-group">
                        <label>Estimated Duration Days</label>
                        <input
                          type="text"
                          className="md-input"
                          style={{ backgroundColor: viewMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}
                          value={formData.estimatedDurationDays}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setFormData({...formData, estimatedDurationDays: value});
                          }}
                          placeholder="Calendar days"
                          disabled={viewMode}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                // Bulk creation form for all phases
                <form id="pep-bulk-form" onSubmit={handleBulkSubmit}>
                  {error && (
                    <div className="md-error" style={{ marginBottom: '16px' }}>
                      {error}
                    </div>
                  )}

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{ ...SECTION_HEADING, margin: '0 0 16px 0' }}>Application Information</h3>

                    {/* Grouping ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{ marginBottom: '8px', display: 'block' }}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          backgroundColor: WS_COLOR.surface,
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.groupingId}
                        onChange={(e) => {
                          setBulkFormData({
                            ...bulkFormData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{ marginBottom: '8px', display: 'block' }}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: !bulkFormData.groupingId ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                        <div
                          onClick={() => {
                            if (bulkFormData.groupingId) {
                              setShowAppDropdown(!showAppDropdown);
                            }
                          }}
                          style={{
                            ...TRIGGER,
                            cursor: bulkFormData.groupingId ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <span style={{ color: bulkFormData.applicationIdCode ? WS_COLOR.text : WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {selectedBulkApp
                              ? (
                                <>
                                  <span>{selectedBulkApp.application_id} - {selectedBulkApp.description}</span>
                                  {selectedBulkApp.disposition && (
                                    <span style={{
                                      ...WS_CHIP,
                                      backgroundColor: getDispositionColor(selectedBulkApp.disposition).bg,
                                      color: getDispositionColor(selectedBulkApp.disposition).color
                                    }}>
                                      {selectedBulkApp.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (bulkFormData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {bulkFormData.groupingId && <Icon icon={icons.ChevronDown} size={16} />}
                        </div>
                        {showAppDropdown && bulkFormData.groupingId && (
                          <div style={MENU}>
                            {applications.filter(app => !lockedApplications.has(lockKey(app.application_id, bulkFormData.groupingId))).length === 0 ? (
                              <div style={{ padding: '12px 16px', color: WS_COLOR.textMuted, ...wsType('table') }}>
                                {applications.length === 0
                                  ? 'No applications available for this group'
                                  : 'All applications in this group are locked (Approved or beyond)'}
                              </div>
                            ) : (
                              applications.map(app => {
                                const isLocked = lockedApplications.has(lockKey(app.application_id, bulkFormData.groupingId));
                                const lockStatus = lockedApplications.get(lockKey(app.application_id, bulkFormData.groupingId));
                                return (
                                  <div
                                    key={app.application_id}
                                    onClick={() => {
                                      if (!isLocked) {
                                        setBulkFormData({...bulkFormData, applicationIdCode: app.application_id});
                                        setShowAppDropdown(false);
                                      }
                                    }}
                                    style={{
                                      ...OPTION,
                                      cursor: isLocked ? 'not-allowed' : 'pointer',
                                      backgroundColor: isLocked ? WS_COLOR.surfaceSubtle : (bulkFormData.applicationIdCode === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface),
                                      opacity: isLocked ? 0.6 : 1
                                    }}
                                    onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle; }}
                                    onMouseLeave={(e) => { if (!isLocked) e.currentTarget.style.backgroundColor = bulkFormData.applicationIdCode === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface; }}
                                    title={isLocked ? `Cannot add effort - application is ${lockStatus}` : ''}
                                  >
                                    <span><strong>{app.application_id}</strong> - {app.description}</span>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                      {isLocked && (
                                        <span style={{
                                          ...WS_CHIP,
                                          backgroundColor: getDeploymentStatusColor(lockStatus).bg,
                                          color: getDeploymentStatusColor(lockStatus).color
                                        }}>
                                          {lockStatus}
                                        </span>
                                      )}
                                      {app.disposition && !isLocked && (
                                        <span style={{
                                          ...WS_CHIP,
                                          backgroundColor: getDispositionColor(app.disposition).bg,
                                          color: getDispositionColor(app.disposition).color
                                        }}>
                                          {app.disposition}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{ ...SECTION_HEADING, margin: '0 0 12px 0' }}>Phase Effort Planning</h3>
                    <p style={{ ...wsType('body'), color: WS_COLOR.textMuted, marginBottom: '16px' }}>
                      Fill Duration for phases to create. Only phases with duration values will create records.
                    </p>

                    <div style={{display: 'grid', gap: '12px'}}>
                      {Object.entries({
                        Assessment_Planning: 'Assessment & Planning',
                        Design_Architecture: 'Design & Architecture',
                        Migration_Execution: 'Migration Execution',
                        Testing_Validation: 'Testing & Validation',
                        GoLive_Optimization: 'Go-Live & Optimization'
                      }).map(([phaseKey, phaseLabel]) => (
                        <div key={phaseKey} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          border: `1px solid ${WS_COLOR.border}`,
                          borderRadius: WS_RADIUS.field,
                          backgroundColor: WS_COLOR.surfaceSubtle
                        }}>
                          <div style={{ ...wsType('label'), color: WS_COLOR.text }}>{phaseLabel}</div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <input
                              type="text"
                              className="md-input"
                              style={{
                                width: '80px',
                                textAlign: 'right'
                              }}
                              value={bulkFormData.phases[phaseKey as keyof typeof bulkFormData.phases].duration}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setBulkFormData({
                                  ...bulkFormData,
                                  phases: {
                                    ...bulkFormData.phases,
                                    [phaseKey]: {
                                      ...bulkFormData.phases[phaseKey as keyof typeof bulkFormData.phases],
                                      duration: value
                                    }
                                  }
                                });
                              }}
                              placeholder="0"
                              title="Duration (days)"
                            />
                            <span style={{ ...wsType('label'), color: WS_COLOR.textMuted, minWidth: '35px' }}>days</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </form>
              )}
      </Sheet>

      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      </div>
      )}
      {dialogs}
    </div>
  );
};

// DW: shared inline shapes for this screen — kit type roles, field outline and menu shadow.
const SECTION_HEADING: React.CSSProperties = { ...wsType('body'), fontWeight: 600 };

const PREVIEW_TH: React.CSSProperties = {
  ...wsType('tableHeader'),
  padding: '8px',
  textAlign: 'left',
  color: WS_COLOR.text
};

const PREVIEW_TD: React.CSSProperties = {
  ...wsType('meta'),
  padding: '8px',
  borderTop: `1px solid ${WS_COLOR.border}`
};

// DW: `.custom-dropdown` itself is drawn as the field by pp-workspace.css; the trigger fills it.
const TRIGGER: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const MENU: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: WS_COLOR.surface,
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: WS_RADIUS.field,
  boxShadow: WS_COLOR.menuShadow,
  zIndex: 1000,
  maxHeight: '200px',
  overflowY: 'auto'
};

const OPTION: React.CSSProperties = {
  ...wsType('table'),
  padding: '12px 16px',
  borderBottom: `1px solid ${WS_COLOR.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

export default PhaseEffortPlanning;
